import { useQuery } from "@tanstack/react-query";
import type { UseQueryResult } from "@tanstack/react-query";

import { API_ROUTES } from "../../api/ApiRoutes";
import type {
  AffiliateKpiCardKey,
  AffiliateKpiValue,
  ApiSuccessEnvelope,
} from "../../types/affiliates";
import {
  getAnalyticsInstance,
  type AffiliateAnalyticsDataSource,
} from "./analyticsDataSource";

export interface AffiliateKpiCardsRange {
  start_date: string;
  end_date: string;
}

// Optional cross-affiliate / cross-code filters. Module-level KPIs take
// both; detail-level KPIs only take `promo_code` because the affiliate
// is already scoped by URL.
export interface AffiliateAnalyticsFilters {
  affiliate?: string;
  promo_code?: string;
}

// Wire shape as of PAMENG-2222: every metric now ships as
// `{ value, change_pct }` where `change_pct` compares the selected range
// against the same window one calendar month back. `value` can be numeric
// (most cards), a date string (FTD_DATE), or null when the metric doesn't
// apply (e.g. CPA_AMOUNT on a non-CPA plan).
interface AffiliateReportingKpiValue {
  value?: number | string | boolean | null;
  change_pct?: number | null;
}
interface AffiliateReportingKpisData {
  metrics?: Record<string, AffiliateReportingKpiValue | null>;
  degraded?: boolean;
}

export type AffiliateKpiCards = Partial<
  Record<AffiliateKpiCardKey, AffiliateKpiValue>
>;

// Unwrap BE's `{value, change_pct}` per metric into the FE tile shape.
// Non-numeric values (FTD_DATE, nulls) are dropped — the overview renders
// only numeric cards. Unknown keys pass through untyped since the BE catalog
// is authoritative (see OperatorAffiliateKpiKey).
const toKpiCards = (data: AffiliateReportingKpisData | undefined) => {
  const metrics = data?.metrics ?? {};
  const cards: AffiliateKpiCards = {};
  Object.entries(metrics).forEach(([key, entry]) => {
    if (entry == null) return;
    const rawValue = entry.value;
    if (rawValue == null) return;
    const value =
      typeof rawValue === "number" ? rawValue : Number(rawValue);
    if (!Number.isFinite(value)) return;
    const change_pct =
      typeof entry.change_pct === "number" ? entry.change_pct : null;
    cards[key as AffiliateKpiCardKey] = { value, change_pct };
  });
  return cards;
};

// Build the axios param object. `undefined` filters are elided so a
// literal `undefined` never reaches the wire as `"undefined"`.
const buildParams = (
  range: AffiliateKpiCardsRange,
  filters: AffiliateAnalyticsFilters = {},
) => {
  const params: Record<string, string> = {
    start_date: range.start_date,
    end_date: range.end_date,
  };
  if (filters.affiliate) params.affiliate = filters.affiliate;
  if (filters.promo_code) params.promo_code = filters.promo_code;
  return params;
};

const fetchKpis = async (
  route: string,
  range: AffiliateKpiCardsRange,
  filters: AffiliateAnalyticsFilters = {},
  dataSource: AffiliateAnalyticsDataSource = "operator",
): Promise<AffiliateKpiCards> => {
  const response = await getAnalyticsInstance(dataSource).get<
    ApiSuccessEnvelope<AffiliateReportingKpisData>
  >(route, { params: buildParams(range, filters) });
  return toKpiCards(response.data.data);
};

// Module-level (all affiliates) KPI cards for the Affiliates list KPIs tab.
export const useAffiliateKpiCards = (
  range: AffiliateKpiCardsRange,
  filters: AffiliateAnalyticsFilters = {},
): UseQueryResult<AffiliateKpiCards, Error> =>
  useQuery<AffiliateKpiCards, Error>({
    queryKey: [
      "affiliate-kpis",
      "cards",
      range.start_date,
      range.end_date,
      filters.affiliate ?? null,
      filters.promo_code ?? null,
    ],
    queryFn: () =>
      fetchKpis(API_ROUTES.AFFILIATE_ANALYTICS_KPIS, range, filters),
    enabled: Boolean(range.start_date && range.end_date),
    staleTime: 1000 * 60,
    refetchOnWindowFocus: false,
  });

// Per-affiliate KPI cards for the profile KPIs panel. Only `promo_code`
// applies — the affiliate is scoped by the URL path.
export const useAffiliateDetailKpiCards = (
  affiliateId: string | undefined,
  range: AffiliateKpiCardsRange,
  filters: Pick<AffiliateAnalyticsFilters, "promo_code"> = {},
): UseQueryResult<AffiliateKpiCards, Error> => {
  // `enabled` guards execution when affiliateId is missing, so the fallback
  // never hits the wire — the local just narrows the type without a cast.
  const safeAffiliateId = affiliateId ?? "";
  return useQuery<AffiliateKpiCards, Error>({
    queryKey: [
      "affiliate-kpis",
      "cards",
      safeAffiliateId,
      range.start_date,
      range.end_date,
      filters.promo_code ?? null,
    ],
    queryFn: () =>
      fetchKpis(
        API_ROUTES.AFFILIATE_DETAIL_ANALYTICS_KPIS.replace(
          "{affiliate_id}",
          encodeURIComponent(safeAffiliateId),
        ),
        range,
        filters,
      ),
    enabled: Boolean(affiliateId && range.start_date && range.end_date),
    staleTime: 1000 * 60,
    refetchOnWindowFocus: false,
  });
};

// Portal KPI cards — the token scopes the query to the signed-in affiliate
// and the BE strips financial metrics (AccessMode=RESTRICTED) before the
// response leaves the server.
export const usePortalKpiCards = (
  range: AffiliateKpiCardsRange,
): UseQueryResult<AffiliateKpiCards, Error> =>
  useQuery<AffiliateKpiCards, Error>({
    queryKey: [
      "affiliate-portal",
      "kpi-cards",
      range.start_date,
      range.end_date,
    ],
    queryFn: () =>
      fetchKpis(API_ROUTES.AFFILIATE_PORTAL_ANALYTICS_KPIS, range, {}, "portal"),
    enabled: Boolean(range.start_date && range.end_date),
    staleTime: 1000 * 60,
    refetchOnWindowFocus: false,
  });

export { fetchKpis, toKpiCards };
